// Library Methods
import { useQuery } from "@tanstack/react-query";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";

// Utils
import { sortByOldestDate } from "src/utils/date/dateHelper";

// Types and API
import { ProgressionExamSummaryType } from "../types";
import { getProgressionExams, getProgressionExternalExams } from "../api";

// Both sources come back in the same shape, so they merge into one
// chronological list the picker can render without knowing which is which.
// Omitting `eye` returns both eyes, which is what the analyses list page needs
// to decide whether a patient has enough exams to offer the action at all.
export const useProgressionExams = (
  clinicId: string | undefined,
  patientId: string | undefined,
  eye?: string
) => {
  const { getToken } = useTokenAccess();

  return useQuery<Array<ProgressionExamSummaryType>>({
    queryKey: ["progression-exams", clinicId, patientId, eye ?? "both"],
    queryFn: async () => {
      if (!clinicId || !patientId) throw new Error();
      const token = await getToken();

      const [exams, externalExams] = await Promise.all([
        getProgressionExams(token, clinicId, patientId, eye),
        getProgressionExternalExams(token, clinicId, patientId, eye),
      ]);

      return sortByOldestDate(
        [...(exams ?? []), ...(externalExams ?? [])],
        "completionDate"
      ) as Array<ProgressionExamSummaryType>;
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
};
